// src/pages/BulkChatSlidesPage.js
// Component for Social Media Image Maker (9:16) with Image Queue Functionality
// Includes optional Phone UI Overlay with Light/Dark Modes & SVG Icons.
// Includes Script Splitting Functionality.
// Includes Hook Overlay Functionality with Glitch Style.
// Includes Scalable Image Messages with Background Bubbles.
// MODIFIED FOR BULK BACKGROUND IMAGE UPLOAD WITH PER-BACKGROUND SETTINGS (PFP, Title, Icon Position)
// NEW: Background settings default to the first image's settings.
// NEW: Bulk generation now creates a single, organized ZIP file.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import JSZip from 'jszip'; // Make sure to install jszip: npm install jszip
import './BulkChatSlidesPage.css';


// --- Constants ----
const EXPORT_WIDTH = 1080;
const EXPORT_HEIGHT = 1920;
const HOST_URL = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'; // Adjust if needed

// Original (colored or black) icons
const BACK_ICON_SRC = `${HOST_URL}/textAppImg/back_button.svg`;
const VIDEO_ICON_SRC = `${HOST_URL}/textAppImg/video_camera.svg`;

// White icons for dark mode
const BACK_ICON_WHITE_SRC = `${HOST_URL}/textAppImg/back_button_white.svg`;
const VIDEO_ICON_WHITE_SRC = `${HOST_URL}/textAppImg/video_camera_white.svg`;

// Hook Colors (From Image)
const TURQUOISE = '#2acde5';
const RED = '#fa2656';

// --- Presets ---
const presets = {
    light: {
        headerColor: '#F0F0F0',
        chatBgColor: '#FFFFFF',
        iconColor: '#0B93F6',
        titleColor: '#000000',
        sentBubble: '#0B93F6',
        sentFont: '#FFFFFF',
        receivedBubble: '#E5E7EB',
        receivedFont: '#000000',
    },
    dark: {
        headerColor: '#1C1C1E',
        chatBgColor: '#000000',
        iconColor: '#FFFFFF',
        titleColor: '#FFFFFF',
        sentBubble: '#0B93F6',
        sentFont: '#FFFFFF',
        receivedBubble: '#3A3A3C',
        receivedFont: '#FFFFFF',
    }
};

const hookPresets = {
    light: { bgColor: 'rgba(255, 255, 255, 0.95)', textColor: '#000000' },
    dark: { bgColor: 'rgba(0, 0, 0, 0.95)', textColor: '#FFFFFF' }
};


// --- Helper Functions ---
const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
    };
};

// Creates a path for a rounded rectangle (doesn't draw/fill)
const drawRoundedRectPath = (ctx, x, y, width, height, radius) => {
    const r = Math.min(radius, width / 2, height / 2);
    const finalRadius = Math.max(0, r);
    ctx.beginPath();
    ctx.moveTo(x + finalRadius, y);
    ctx.lineTo(x + width - finalRadius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + finalRadius);
    ctx.lineTo(x + width, y + height - finalRadius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - finalRadius, y + height);
    ctx.lineTo(x + finalRadius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - finalRadius);
    ctx.lineTo(x, y + finalRadius);
    ctx.quadraticCurveTo(x, y, x + finalRadius, y);
    ctx.closePath();
};

// Draws (fills) a rounded rectangle
const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    drawRoundedRectPath(ctx, x, y, width, height, radius);
    ctx.fill();
};


const wrapText = (ctx, text, maxWidth) => {
    if (!text) return [''];
    const words = text.split(' ');
    const lines = [];
    if (words.length === 0) return [''];
    let currentLine = words[0] || '';
    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        if (!word) continue;
        const testLine = currentLine + " " + word;
        const width = ctx.measureText(testLine).width;
        if (width < maxWidth || currentLine === '') {
            currentLine = testLine;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

const calculateBubbleWidth = (ctx, lines, bubblePaddingX) => {
    let maxWidth = 0;
    lines.forEach(line => {
        const lineWidth = ctx.measureText(line).width;
        if (lineWidth > maxWidth) maxWidth = lineWidth;
    });
    return maxWidth + 2 * bubblePaddingX;
};

// Helper to calculate message height for splitting logic (Updated for Image Scaling)
const calculateMessageHeightInternal = (msg, ctx, messageMaxWidth, bubblePaddingX, lineHeight, fontSize, bubblePaddingY, imgScale) => {
    const messageType = msg && msg.type ? msg.type : 'text';

    if (messageType === 'image') {
        if (!msg.width || !msg.height) return 150; // Default height

        const maxImgWidth = messageMaxWidth * 0.95; // Max width *before* scaling
        const scalePercentage = imgScale / 100;

        let imgWidth = msg.width;
        let imgHeight = msg.height;
        const ratio = imgWidth / imgHeight;

        if (imgWidth > maxImgWidth) {
            imgWidth = maxImgWidth;
            imgHeight = imgWidth / ratio;
        }

        imgWidth *= scalePercentage;
        imgHeight *= scalePercentage;

        // Add bubble padding height
        return imgHeight + 2 * bubblePaddingY;
    } else {
        const lines = wrapText(ctx, msg.text, messageMaxWidth - 2 * bubblePaddingX);
        const textBlockHeight = lines.length * lineHeight - (lineHeight - fontSize);
        const bubbleHeight = textBlockHeight + 2 * bubblePaddingY;
        return bubbleHeight;
    }
};


// --- Component ---
const BulkChatSlidesPage = () => {
    // Refs
    const bulkImageUploadRef = useRef(null);
    const blurRangeRef = useRef(null);
    const brightnessRangeRef = useRef(null);
    const contrastRangeRef = useRef(null);
    const saturateRangeRef = useRef(null);
    const grayscaleRangeRef = useRef(null);
    const messageTextRef = useRef(null);
    const messageTypeToggleRef = useRef(null);
    const sentBubbleColorPickerRef = useRef(null);
    const sentFontColorPickerRef = useRef(null);
    const receivedBubbleColorPickerRef = useRef(null);
    const receivedFontColorPickerRef = useRef(null);
    const fontSizeMultiplierSliderRef = useRef(null);
    const canvasRef = useRef(null);
    const messageListContainerRef = useRef(null);
    const exportFilenameRef = useRef(null);
    const bubbleRoundnessSliderRef = useRef(null);
    const messageTopMarginSliderRef = useRef(null);
    const phoneOverlayToggleRef = useRef(null);
    const profileImageUploadRef = useRef(null);
    const headerColorPickerRef = useRef(null);
    const chatBgColorPickerRef = useRef(null);
    const chatBgOpacitySliderRef = useRef(null);
    const phoneUiOffsetYSliderRef = useRef(null);
    const scriptUploadRef = useRef(null);
    const phoneUiWidthSliderRef = useRef(null);
    const phoneUiHeightSliderRef = useRef(null);
    const phoneUiHeaderPaddingSliderRef = useRef(null);
    const hookOverlayToggleRef = useRef(null);
    const hookTextRef = useRef(null);
    const hookVerticalPositionSliderRef = useRef(null);
    const hookOpacitySliderRef = useRef(null);
    const hookFontSizeMultiplierSliderRef = useRef(null);
    const imageMessageUploadRef = useRef(null);
    const imageScaleSliderRef = useRef(null);
    const iconOffsetYSliderRef = useRef(null);


    // State
    const [backgroundImages, setBackgroundImages] = useState([]);
    const [selectedBgImageIndex, setSelectedBgImageIndex] = useState(0);
    const currentBackgroundData = backgroundImages[selectedBgImageIndex] || null;
    const [messages, setMessages] = useState([
        { id: 0, text: "Hey, are you free to talk?", sender: 'sent', type: 'text' },
        { id: 1, text: "What's going on?", sender: 'received', type: 'text' },
    ]);
    const [messageIdCounter, setMessageIdCounter] = useState(2);
    const [draggedMessageId, setDraggedMessageId] = useState(null);
    const [currentFontSizeMultiplier, setCurrentFontSizeMultiplier] = useState(1.0);
    const [msgStyles, setMsgStyles] = useState({
        sent: { bubble: presets.light.sentBubble, font: presets.light.sentFont },
        received: { bubble: presets.light.receivedBubble, font: presets.light.receivedFont }
    });
    const [exportFilename, setExportFilename] = useState(`chatslide_${Date.now()}`);
    const [bubbleRoundness, setBubbleRoundness] = useState(25);
    const [messageTopMargin, setMessageTopMargin] = useState(25);
    const [blurVal, setBlurVal] = useState(0);
    const [brightnessVal, setBrightnessVal] = useState(100);
    const [contrastVal, setContrastVal] = useState(100);
    const [saturateVal, setSaturateVal] = useState(100);
    const [grayscaleVal, setGrayscaleVal] = useState(0);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [stylingOpen, setStylingOpen] = useState(false);
    const [phoneUIOpen, setPhoneUIOpen] = useState(true);
    const [currentImageName, setCurrentImageName] = useState('');
    const [imageQueue, setImageQueue] = useState([]);
    const [showPhoneOverlay, setShowPhoneOverlay] = useState(true);
    const [headerColor, setHeaderColor] = useState(presets.light.headerColor);
    const [chatBgColor, setChatBgColor] = useState(presets.light.chatBgColor);
    const [chatBgOpacity, setChatBgOpacity] = useState(100);
    const [iconColor, setIconColor] = useState(presets.light.iconColor);
    const [titleColor, setTitleColor] = useState(presets.light.titleColor);
    const [backIconImg, setBackIconImg] = useState(null);
    const [videoIconImg, setVideoIconImg] = useState(null);
    const [phoneUiOffsetY, setPhoneUiOffsetY] = useState(0);
    const [phoneUiWidth, setPhoneUiWidth] = useState(90);
    const [phoneUiHeight, setPhoneUiHeight] = useState(70);
    const [headerPaddingScale, setHeaderPaddingScale] = useState(50); // Default set to 50
    const [hookOverlayOpen, setHookOverlayOpen] = useState(false); // Default set to false
    const [showHookOverlay, setShowHookOverlay] = useState(false); // Default set to false
    const [hookText, setHookText] = useState("How to hook slide");
    const [hookMode, setHookMode] = useState('dark');
    const [hookVerticalPosition, setHookVerticalPosition] = useState(15);
    const [hookOpacity, setHookOpacity] = useState(95);
    const [hookFontSizeMultiplier, setHookFontSizeMultiplier] = useState(1.0);
    const [imageUploadOpen, setImageUploadOpen] = useState(true);
    const [selectedImageFiles, setSelectedImageFiles] = useState([]);
    const [imageSenderType, setImageSenderType] = useState('received');
    const [imageScale, setImageScale] = useState(80);


    // --- Icon Loading Effect based on iconColor ---
    useEffect(() => {
        const loadActualIcon = (src, setter) => {
            const img = new Image();
            img.onload = () => setter(img);
            img.onerror = () => {
                console.error(`Failed to load icon: ${src}. Check path & ensure file exists in /public/textAppImg/`);
                setter(null);
            }
            img.src = src;
        };

        const isDarkModeRequiringWhiteIcons = iconColor.toLowerCase() === '#ffffff';

        loadActualIcon(isDarkModeRequiringWhiteIcons ? BACK_ICON_WHITE_SRC : BACK_ICON_SRC, setBackIconImg);
        loadActualIcon(isDarkModeRequiringWhiteIcons ? VIDEO_ICON_WHITE_SRC : VIDEO_ICON_SRC, setVideoIconImg);

    }, [iconColor]);


    // --- Apply Preset Function ---
    const applyPreset = (mode) => {
        const preset = presets[mode];
        setHeaderColor(preset.headerColor);
        setChatBgColor(preset.chatBgColor);
        setIconColor(preset.iconColor);
        setTitleColor(preset.titleColor);
        setMsgStyles({
            sent: { bubble: preset.sentBubble, font: preset.sentFont },
            received: { bubble: preset.receivedBubble, font: preset.receivedFont }
        });
        setChatBgOpacity(100);
        setPhoneUiOffsetY(0);
        setPhoneUiWidth(90);
        setPhoneUiHeight(70);
        setHeaderPaddingScale(50); // Default set to 50
    };


    // --- PFP Drawing (FIXED ASPECT RATIO) ---
    const drawProfilePicture = (ctx, pfpImage, x, y, size) => {
        ctx.save();
        ctx.beginPath();
        ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();

        if (pfpImage && pfpImage.complete && pfpImage.naturalWidth > 0) {
            const imgWidth = pfpImage.naturalWidth;
            const imgHeight = pfpImage.naturalHeight;
            const imgRatio = imgWidth / imgHeight;
            const targetRatio = 1;

            let sW = imgWidth, sH = imgHeight, sX = 0, sY = 0;
            let dW = size, dH = size, dX = x, dY = y;

            if (imgRatio > targetRatio) {
                sW = imgHeight * targetRatio;
                sX = (imgWidth - sW) / 2;
            } else if (imgRatio < targetRatio) {
                sH = imgWidth / targetRatio;
                sY = (imgHeight - sH) / 2;
            }
            ctx.drawImage(pfpImage, sX, sY, sW, sH, dX, dY, dW, dH);

        } else {
            ctx.fillStyle = '#A0AEC0';
            ctx.fillRect(x, y, size, size);
            ctx.fillStyle = '#FFFFFF';
            ctx.font = `bold ${size * 0.4}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('PFP', x + size / 2, y + size / 2 + size * 0.05);
        }
        ctx.restore();
    };


    // --- Message Drawing (Updated for Scaled Images & Bubbles) ---
    const drawMessages = (ctx, areaWidth, areaHeight, activeScaleFactor, isExporting, startY, sidePadding, usableWidth, messagesToRender, currentImageScale) => {
        const bottomPadding = areaHeight * 0.04;
        const messageMaxWidth = usableWidth * 0.75;
        const messageSpacing = areaHeight * 0.015;

        const previewCanvasWidth = canvasRef.current ? canvasRef.current.width : 360;
        const previewCanvasHeight = canvasRef.current ? canvasRef.current.height : 640;

        let baseFontSizeForPreview = Math.max(12, Math.min(previewCanvasWidth * 0.045, previewCanvasHeight * 0.025));
        let dynamicFontSize = baseFontSizeForPreview * currentFontSizeMultiplier;
        if (isExporting) { dynamicFontSize *= activeScaleFactor; }
        const fontSize = Math.floor(dynamicFontSize);
        const lineHeight = fontSize * 1.3;
        const bubblePaddingY = fontSize * 0.5;
        const bubblePaddingX = fontSize * 0.8;

        ctx.font = `500 ${fontSize}px Inter, sans-serif`;
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';

        let currentY = startY;

        for (let i = 0; i < messagesToRender.length; i++) {
            const msg = messagesToRender[i];
            const dynamicRadius = bubbleRoundness * (isExporting ? activeScaleFactor : 1);
            let bubbleX;
            const currentStyle = msg.sender === 'sent' ? msgStyles.sent : msgStyles.received;

            // --- IMAGE MESSAGE DRAWING ---
            if (msg.type === 'image' && msg.imgObject && msg.imgObject.complete) {
                const maxImgWidth = messageMaxWidth * 0.95; // Max width *before* user scaling
                const scalePercentage = currentImageScale / 100;

                let imgWidth = msg.imgObject.naturalWidth;
                let imgHeight = msg.imgObject.naturalHeight;
                const ratio = imgWidth / imgHeight;

                // Constrain by max width first
                if (imgWidth > maxImgWidth) {
                    imgWidth = maxImgWidth;
                    imgHeight = imgWidth / ratio;
                }

                // Apply user scaling
                imgWidth *= scalePercentage;
                imgHeight *= scalePercentage;

                // Calculate bubble size (Image + Padding)
                const bubbleWidth = imgWidth + 2 * bubblePaddingX;
                const bubbleHeight = imgHeight + 2 * bubblePaddingY;

                if (currentY + bubbleHeight > areaHeight - bottomPadding && i > 0) break;

                // Calculate X position for bubble
                if (msg.sender === 'sent') { bubbleX = areaWidth - sidePadding - bubbleWidth; }
                else { bubbleX = sidePadding; }

                // Calculate Image X/Y *inside* the bubble
                const imageX = bubbleX + bubblePaddingX;
                const imageY = currentY + bubblePaddingY;

                // Draw Bubble Background
                ctx.fillStyle = currentStyle.bubble;
                drawRoundedRect(ctx, bubbleX, currentY, bubbleWidth, bubbleHeight, dynamicRadius);

                // Draw Image on top
                ctx.drawImage(msg.imgObject, imageX, imageY, imgWidth, imgHeight);

                currentY += (bubbleHeight + messageSpacing);
            }
            // --- IMAGE LOADING/ERROR PLACEHOLDER ---
            else if (msg.type === 'image') {
                 const placeholderHeight = 100 * (isExporting ? activeScaleFactor : 1);
                 const placeholderWidth = 150 * (isExporting ? activeScaleFactor : 1);
                 if (currentY + placeholderHeight > areaHeight - bottomPadding && i > 0) break;

                 if (msg.sender === 'sent') { bubbleX = areaWidth - sidePadding - placeholderWidth; }
                 else { bubbleX = sidePadding; }

                 ctx.fillStyle = '#555';
                 drawRoundedRect(ctx, bubbleX, currentY, placeholderWidth, placeholderHeight, dynamicRadius);
                 ctx.fillStyle = '#FFF';
                 ctx.font = `italic ${12 * (isExporting ? activeScaleFactor : 1)}px Inter, sans-serif`;
                 ctx.textAlign = 'center';
                 ctx.textBaseline = 'middle';
                 ctx.fillText("[Loading Image]", bubbleX + placeholderWidth / 2, currentY + placeholderHeight / 2);
                 ctx.textAlign = 'left'; // Reset
                 ctx.textBaseline = 'top'; // Reset
                 ctx.font = `500 ${fontSize}px Inter, sans-serif`; // Reset font

                 currentY += (placeholderHeight + messageSpacing);
            }
            // --- TEXT MESSAGE DRAWING ---
            else {
                const lines = wrapText(ctx, msg.text, messageMaxWidth - 2 * bubblePaddingX);
                const textBlockHeight = lines.length * lineHeight - (lineHeight - fontSize);
                const bubbleHeight = textBlockHeight + 2 * bubblePaddingY;
                const bubbleWidth = calculateBubbleWidth(ctx, lines, bubblePaddingX);

                if (currentY + bubbleHeight > areaHeight - bottomPadding && i > 0) break;

                ctx.fillStyle = currentStyle.bubble;

                if (msg.sender === 'sent') { bubbleX = areaWidth - sidePadding - bubbleWidth; }
                else { bubbleX = sidePadding; }

                drawRoundedRect(ctx, bubbleX, currentY, bubbleWidth, bubbleHeight, dynamicRadius);

                ctx.fillStyle = currentStyle.font;
                const capturedY = currentY;
                lines.forEach((line, index) => {
                    const textY = capturedY + bubblePaddingY + (index * lineHeight);
                    ctx.fillText(line, bubbleX + bubblePaddingX, textY);
                });
                currentY += (bubbleHeight + messageSpacing);
            }
        }
    };


    // --- Hook Overlay Drawing (Glitch Style) ---
    const drawHookOverlay = (drawCtx, targetWidth, targetHeight, activeScaleFactor, shouldShowHook) => {
        if (!shouldShowHook || !hookText.trim()) return;

        const preset = hookPresets[hookMode];

        // 1. Setup Font and Calculate Text Dimensions
        const baseFontSize = Math.floor(targetWidth * 0.065);
        const fontSize = Math.floor(baseFontSize * hookFontSizeMultiplier);
        const lineHeight = fontSize * 1.25;
        drawCtx.font = `bold ${fontSize}px Inter, sans-serif`;
        drawCtx.textAlign = 'center';
        drawCtx.textBaseline = 'middle';

        const maxWidth = targetWidth * 0.8;
        const lines = wrapText(drawCtx, hookText, maxWidth);
        const textBlockHeight = lines.length * lineHeight;

        let actualTextWidth = 0;
        lines.forEach(line => {
            const lineWidth = drawCtx.measureText(line).width;
            if (lineWidth > actualTextWidth) actualTextWidth = lineWidth;
        });

        // 2. Calculate Box Dimensions
        const paddingX = 40 * activeScaleFactor;
        const paddingY = 30 * activeScaleFactor;
        const boxWidth = actualTextWidth + 2 * paddingX;
        const boxHeight = textBlockHeight + 2 * paddingY;
        const boxX = (targetWidth - boxWidth) / 2;
        const offset = 6 * activeScaleFactor;

        // 3. Calculate Y Position (based on slider 0-100%)
        const maxBoxY = targetHeight - boxHeight - offset;
        const minBoxY = offset;
        const boxY = minBoxY + ((maxBoxY - minBoxY) * (hookVerticalPosition / 100));

        // 4. Draw Elements
        drawCtx.save();
        drawCtx.globalAlpha = hookOpacity / 100;

        // Draw Turquoise Shadow (Top-Left)
        drawCtx.fillStyle = TURQUOISE;
        drawCtx.fillRect(boxX - offset, boxY - offset, boxWidth, boxHeight);

        // Draw Red Shadow (Bottom-Right)
        drawCtx.fillStyle = RED;
        drawCtx.fillRect(boxX + offset, boxY + offset, boxWidth, boxHeight);

        // Draw Main Box
        drawCtx.fillStyle = preset.bgColor;
        drawRoundedRect(drawCtx, boxX, boxY, boxWidth, boxHeight, 10 * activeScaleFactor); // Added roundness

        drawCtx.restore();

        // 5. Draw Text
        drawCtx.fillStyle = preset.textColor;
        const textX = boxX + boxWidth / 2;
        const textBlockStartY = boxY + (boxHeight - textBlockHeight) / 2;

        lines.forEach((line, index) => {
            drawCtx.fillText(line, textX, textBlockStartY + (index * lineHeight) + (lineHeight / 2));
        });
    };


    // --- Canvas Drawing ---
    const drawCanvas = useCallback((isExporting = false, messagesToDraw = null, options = {}) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const messagesForDrawing = messagesToDraw || messages;
        const backgroundToDraw = options.backgroundImage || currentBackgroundData; // Use passed background if available, otherwise current

        const targetWidth = isExporting ? EXPORT_WIDTH : canvas.width;
        const targetHeight = isExporting ? EXPORT_HEIGHT : canvas.height;
        const activeScaleFactor = isExporting && canvas.width > 0 ? (EXPORT_WIDTH / canvas.width) : 1;

        const exportCanvas = isExporting ? document.createElement('canvas') : canvas;
        exportCanvas.width = targetWidth;
        exportCanvas.height = targetHeight;
        const drawCtx = exportCanvas.getContext('2d');

        // Get per-background settings or fallback to defaults
        const currentChatTitle = backgroundToDraw ? backgroundToDraw.chatTitle : "Chat";
        const currentProfileImage = backgroundToDraw ? backgroundToDraw.profileImage : null;
        const currentIconOffsetY = backgroundToDraw ? backgroundToDraw.iconOffsetY : 0;


        // Determine if hook should show for *this specific draw call*
        const shouldShowHookForThisDraw = options.hasOwnProperty('showHook')
            ? options.showHook
            : showHookOverlay;

        // 1. Draw Base Background
        drawCtx.fillStyle = '#111827';
        drawCtx.fillRect(0, 0, targetWidth, targetHeight);
        if (backgroundToDraw && backgroundToDraw.image && backgroundToDraw.image.complete && backgroundToDraw.image.naturalWidth > 0) {
            drawCtx.filter = `blur(${blurVal}px) brightness(${brightnessVal}%) contrast(${contrastVal}%) saturate(${saturateVal}%) grayscale(${grayscaleVal}%)`;
            const imgWidth = backgroundToDraw.image.naturalWidth; const imgHeight = backgroundToDraw.image.naturalHeight;
            const imgAspectRatio = imgWidth / imgHeight; const canvasAspectRatio = targetWidth / targetHeight;
            let sX = 0, sY = 0, sW = imgWidth, sH = imgHeight; let dX = 0, dY = 0, dW = targetWidth, dH = targetHeight;
            if (imgAspectRatio > canvasAspectRatio) { sW = imgHeight * canvasAspectRatio; sX = (imgWidth - sW) / 2; }
            else if (imgAspectRatio < canvasAspectRatio) { sH = imgWidth / canvasAspectRatio; sY = (imgHeight - sH) / 2; }
            drawCtx.drawImage(backgroundToDraw.image, sX, sY, sW, sH, dX, dY, dW, dH);
            drawCtx.filter = 'none';
        } else if (!backgroundToDraw && !isExporting) {
            drawCtx.font = `${targetWidth * 0.04}px Inter, sans-serif`; drawCtx.fillStyle = '#9ca3af';
            drawCtx.textAlign = 'center'; drawCtx.textBaseline = 'middle';
            drawCtx.fillText('Upload background image(s)', targetWidth / 2, targetHeight / 2);
        }

        // 2. Draw Phone UI Overlay or Direct Messages
        if (showPhoneOverlay) {
            const chatRectWidth = targetWidth * (phoneUiWidth / 100);
            const chatRectHeight = targetHeight * (phoneUiHeight / 100);
            const chatRectX = (targetWidth - chatRectWidth) / 2;
            const baseInsetPaddingY = targetHeight * 0.06;
            const yOffset = targetHeight * (phoneUiOffsetY / 100);
            const chatRectY = baseInsetPaddingY + yOffset;
            const chatRectRadius = 25 * activeScaleFactor;

            const pfpSize = chatRectWidth * 0.16;
            const titleFontSize = Math.floor(chatRectWidth * 0.05);
            const iconSize = chatRectWidth * 0.08;
            const paddingScale = headerPaddingScale / 100;
            const headerPaddingY = (25 * activeScaleFactor) * paddingScale;
            const pfpTitleGap = (5 * activeScaleFactor) * paddingScale;
            const headerHeight = headerPaddingY + pfpSize + pfpTitleGap + titleFontSize + headerPaddingY;

            drawCtx.save();
            drawCtx.shadowColor = 'rgba(0, 0, 0, 0.3)'; drawCtx.shadowBlur = 15 * activeScaleFactor;
            drawCtx.shadowOffsetX = 0; drawCtx.shadowOffsetY = 5 * activeScaleFactor;
            drawCtx.globalAlpha = chatBgOpacity / 100;
            drawCtx.fillStyle = chatBgColor;
            drawRoundedRect(drawCtx, chatRectX, chatRectY, chatRectWidth, chatRectHeight, chatRectRadius);
            drawCtx.globalAlpha = 1.0;
            drawCtx.restore();

            drawCtx.fillStyle = headerColor;
            drawCtx.beginPath();
            drawCtx.moveTo(chatRectX, chatRectY + headerHeight);
            drawCtx.lineTo(chatRectX, chatRectY + chatRectRadius);
            drawCtx.quadraticCurveTo(chatRectX, chatRectY, chatRectX + chatRectRadius, chatRectY);
            drawCtx.lineTo(chatRectX + chatRectWidth - chatRectRadius, chatRectY);
            drawCtx.quadraticCurveTo(chatRectX + chatRectWidth, chatRectY, chatRectX + chatRectWidth, chatRectY + chatRectRadius);
            drawCtx.lineTo(chatRectX + chatRectWidth, chatRectY + headerHeight);
            drawCtx.closePath();
            drawCtx.fill();

            const iconPadding = 25 * activeScaleFactor;
            const iconY = chatRectY + headerPaddingY - (iconSize / 1.5) + (currentIconOffsetY * activeScaleFactor); // Adjusted by currentIconOffsetY
            const backIconX = chatRectX + iconPadding;
            const videoIconX = chatRectX + chatRectWidth - iconPadding - iconSize;
            const pfpX = chatRectX + chatRectWidth / 2 - pfpSize / 2;
            const pfpY = chatRectY + headerPaddingY;
            const titleX = chatRectX + chatRectWidth / 2;
            const titleY = pfpY + pfpSize + pfpTitleGap + titleFontSize / 2;

            if (backIconImg && backIconImg.complete && backIconImg.naturalWidth > 0) {
                 drawCtx.drawImage(backIconImg, backIconX, iconY, iconSize, iconSize);
            }
            if (videoIconImg && videoIconImg.complete && videoIconImg.naturalWidth > 0) {
                drawCtx.drawImage(videoIconImg, videoIconX, iconY, iconSize, iconSize);
            }

            drawProfilePicture(drawCtx, currentProfileImage, pfpX, pfpY, pfpSize); // Use currentProfileImage
            drawCtx.fillStyle = titleColor;
            drawCtx.font = `600 ${titleFontSize}px Inter, sans-serif`;
            drawCtx.textAlign = 'center';
            drawCtx.textBaseline = 'middle';
            drawCtx.fillText(currentChatTitle, titleX, titleY); // Use currentChatTitle

            const messageAreaX = chatRectX;
            const messageAreaY = chatRectY + headerHeight;
            const messageAreaW = chatRectWidth;
            const messageAreaH = chatRectHeight - headerHeight;
            const messageStartGap = 15 * activeScaleFactor;
            const messageInternalPadding = messageAreaW * 0.04;

            drawCtx.save();
            drawCtx.beginPath();
            drawCtx.moveTo(messageAreaX, messageAreaY);
            drawCtx.lineTo(messageAreaX + messageAreaW, messageAreaY);
            drawCtx.lineTo(messageAreaX + messageAreaW, messageAreaY + messageAreaH - chatRectRadius);
            drawCtx.quadraticCurveTo(messageAreaX + messageAreaW, messageAreaY + messageAreaH, messageAreaX + messageAreaW - chatRectRadius, messageAreaY + messageAreaH);
            drawCtx.lineTo(messageAreaX + chatRectRadius, messageAreaY + messageAreaH);
            drawCtx.quadraticCurveTo(messageAreaX, messageAreaY + messageAreaH, messageAreaX, messageAreaY + messageAreaH - chatRectRadius);
            drawCtx.lineTo(messageAreaX, messageAreaY);
            drawCtx.closePath();
            drawCtx.clip();

            drawCtx.translate(messageAreaX, messageAreaY);
            drawMessages(drawCtx, messageAreaW, messageAreaH, activeScaleFactor, isExporting, messageStartGap, messageInternalPadding, messageAreaW - (2 * messageInternalPadding), messagesForDrawing, imageScale);
            drawCtx.restore();

        } else {
            const startY = targetHeight * (messageTopMargin / 100);
            const sidePadding = targetWidth * 0.04;
            drawMessages(drawCtx, targetWidth, targetHeight, activeScaleFactor, isExporting, startY, sidePadding, targetWidth - (2 * sidePadding), messagesForDrawing, imageScale);
        }

        // 3. Draw Hook Overlay (Always on top)
        drawHookOverlay(drawCtx, targetWidth, targetHeight, activeScaleFactor, shouldShowHookForThisDraw);

        if (isExporting) return exportCanvas;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBackgroundData, messages, msgStyles, currentFontSizeMultiplier, blurVal, brightnessVal, contrastVal, saturateVal, grayscaleVal, bubbleRoundness, messageTopMargin, showPhoneOverlay, headerColor, chatBgColor, chatBgOpacity, iconColor, titleColor, backIconImg, videoIconImg, phoneUiOffsetY, phoneUiWidth, phoneUiHeight, headerPaddingScale, showHookOverlay, hookText, hookMode, hookVerticalPosition, hookOpacity, hookFontSizeMultiplier, imageScale]);


    // --- Main useEffect for canvas redraw on state changes ---
    useEffect(() => {
        const needsRedraw = messages.some(msg => msg.type === 'image' && (!msg.imgObject || !msg.imgObject.complete)) ||
                             (currentBackgroundData && (!currentBackgroundData.image || !currentBackgroundData.image.complete)) ||
                             (currentBackgroundData && currentBackgroundData.profileImage && (!currentBackgroundData.profileImage.complete));
        drawCanvas();

        if (needsRedraw) {
           const timer = setTimeout(() => drawCanvas(), 100);
           return () => clearTimeout(timer);
        }
    }, [drawCanvas, messages, currentBackgroundData]);


    // --- Handlers (Uploads, Messages, Queue, Drag/Drop) ---
    const handleBulkImageUpload = (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        if (files.length > 12) {
            alert("Please select a maximum of 12 background images.");
            event.target.value = null;
            return;
        }

        let loadedCount = 0;
        const tempBackgrounds = [];
        const firstBgSettings = backgroundImages[0] || {};
        const newImageChatTitle = backgroundImages.length > 0 ? firstBgSettings.chatTitle : "Chat";
        const newImagePfp = backgroundImages.length > 0 ? firstBgSettings.profileImage : null;
        const newImageIconOffset = backgroundImages.length > 0 ? firstBgSettings.iconOffsetY : 20;


        files.forEach((file, index) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    tempBackgrounds[index] = {
                        name: file.name,
                        src: e.target.result,
                        image: img,
                        profileImage: newImagePfp,
                        chatTitle: newImageChatTitle,
                        iconOffsetY: newImageIconOffset,
                    };
                    loadedCount++;
                    if (loadedCount === files.length) {
                        const newBgs = backgroundImages.length > 0
                            ? [...backgroundImages, ...tempBackgrounds.filter(Boolean)]
                            : tempBackgrounds.filter(Boolean);

                        setBackgroundImages(newBgs);
                        if (backgroundImages.length === 0) {
                             setSelectedBgImageIndex(0);
                        }
                        alert(`Loaded ${loadedCount} background image(s).`);
                    }
                };
                img.onerror = () => {
                    console.error(`Error loading background image: ${file.name}`);
                    alert(`Error loading image: ${file.name}. It might be corrupted or unsupported.`);
                    loadedCount++;
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
        event.target.value = null;
    };

    const handleProfileImageUploadForBg = (event) => {
        const file = event.target.files[0];
        if (!file || !currentBackgroundData) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const newPfp = img;
                setBackgroundImages(prevBgs => {
                    if (selectedBgImageIndex !== 0) {
                         return prevBgs.map((bg, idx) =>
                            idx === selectedBgImageIndex ? { ...bg, profileImage: newPfp } : bg
                        );
                    }
                    const oldPfpOfFirstBg = prevBgs[0].profileImage;
                    const newBgs = prevBgs.map(bg => {
                        if (bg.profileImage === oldPfpOfFirstBg) {
                            return { ...bg, profileImage: newPfp };
                        }
                        return bg;
                    });
                    newBgs[0] = { ...newBgs[0], profileImage: newPfp };
                    return newBgs;
                });
            };
            img.onerror = () => {
                console.error("Error loading profile image for background.");
                alert("Error loading profile image for background.");
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
        event.target.value = null;
    };

    // --- CORRECTED/ROBUST SETTINGS PROPAGATION ---
    const handleChatTitleChangeForBg = (e) => {
        const newTitle = e.target.value;
        if (!currentBackgroundData) return;
        
        setBackgroundImages(prevBgs => {
            if (selectedBgImageIndex !== 0) {
                return prevBgs.map((bg, idx) =>
                    idx === selectedBgImageIndex ? { ...bg, chatTitle: newTitle } : bg
                );
            }
            const oldTitleOfFirstBg = prevBgs[0].chatTitle;
            const newBgs = prevBgs.map(bg => {
                if (bg.chatTitle === oldTitleOfFirstBg) {
                    return { ...bg, chatTitle: newTitle };
                }
                return bg;
            });
            newBgs[0] = { ...newBgs[0], chatTitle: newTitle };
            return newBgs;
        });
    };

    const handleIconOffsetYChangeForBg = (e) => {
        const newOffset = parseInt(e.target.value, 10);
        if (!currentBackgroundData || isNaN(newOffset)) return;

        setBackgroundImages(prevBgs => {
            if (selectedBgImageIndex !== 0) {
                return prevBgs.map((bg, idx) =>
                    idx === selectedBgImageIndex ? { ...bg, iconOffsetY: newOffset } : bg
                );
            }
            const oldOffsetOfFirstBg = prevBgs[0].iconOffsetY;
            const newBgs = prevBgs.map(bg => {
                if (bg.iconOffsetY === oldOffsetOfFirstBg) {
                    return { ...bg, iconOffsetY: newOffset };
                }
                return bg;
            });
            newBgs[0] = { ...newBgs[0], iconOffsetY: newOffset };
            return newBgs;
        });
    };


    const handleAddMessage = () => {
        const text = messageTextRef.current.value.trim();
        const sender = messageTypeToggleRef.current.checked ? 'sent' : 'received';
        if (text) {
            setMessages(prev => [...prev, { id: messageIdCounter, text, sender, type: 'text' }]);
            setMessageIdCounter(prev => prev + 1);
            messageTextRef.current.value = '';
        } else { alert("Please enter some text for the message."); }
    };
    const handleRemoveLastMessage = () => {
        if (messages.length > 0) setMessages(prev => prev.slice(0, -1));
        else alert("No messages to remove.");
    };
    const handleClearAllMessages = () => {
        if (messages.length > 0) {
            if (window.confirm("Are you sure you want to clear all messages?")) setMessages([]);
        } else { alert("No messages to clear."); }
    };
    const handleExportImage = () => {
        if (!currentBackgroundData) { alert("Please upload a background image first."); return; }
        const exportCanvasElement = drawCanvas(true);
        if (!exportCanvasElement) { alert("Canvas not ready for export."); return; }
        const dataURL = exportCanvasElement.toDataURL('image/jpeg', 0.9);
        const link = document.createElement('a');
        link.href = dataURL;
        const finalFilename = exportFilename.toLowerCase().endsWith('.jpeg') || exportFilename.toLowerCase().endsWith('.jpg')
            ? exportFilename : `${exportFilename}.jpeg`;
        link.download = finalFilename;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };
    const handleAddImageToQueue = () => {
        if (!currentBackgroundData) { alert("Please upload a background image first."); return; }
        if (!currentImageName.trim()) { alert("Please enter a name for the image before adding to queue."); return; }
        const exportCanvasElement = drawCanvas(true);
        if (!exportCanvasElement) { alert("Canvas not ready to be added to queue."); return; }
        const dataURL = exportCanvasElement.toDataURL('image/jpeg', 0.9);
        setImageQueue(prevQueue => [...prevQueue, { name: currentImageName.trim(), dataUrl: dataURL }]);
        setCurrentImageName('');
        alert(`"${currentImageName.trim()}" added to queue! Queue size: ${imageQueue.length + 1}`);
    };
    const handleRemoveFromQueue = (indexToRemove) => {
        setImageQueue(prevQueue => prevQueue.filter((_, index) => index !== indexToRemove));
    };
    const handleDownloadQueue = () => {
        if (imageQueue.length === 0) { alert("Save queue is empty. Add images first."); return; }
        imageQueue.forEach((image, index) => {
            setTimeout(() => {
                const link = document.createElement('a'); link.href = image.dataUrl;
                const filename = image.name.toLowerCase().endsWith('.jpeg') || image.name.toLowerCase().endsWith('.jpg')
                    ? image.name : `${image.name}.jpeg`;
                link.download = filename;
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
            }, index * 300);
        });
        if (window.confirm("All images from the queue have been initiated for download. Clear the queue now?")) { setImageQueue([]); }
    };

    const handleDragStart = (e, id) => {
        setDraggedMessageId(id);
        e.target.classList.add('opacity-50', 'bg-sky-700/50');
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = (e) => {
        e.target.classList.remove('opacity-50', 'bg-sky-700/50');
        clearDropIndicator();
        setDraggedMessageId(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const afterElement = getDragAfterElement(messageListContainerRef.current, e.clientY);
        clearDropIndicator();
        const indicator = document.createElement('div');
        indicator.className = 'drop-indicator-slides';
        if (afterElement == null) {
            messageListContainerRef.current.appendChild(indicator);
        } else {
            messageListContainerRef.current.insertBefore(indicator, afterElement);
        }
    };

    const clearDropIndicator = () => {
        const indicator = messageListContainerRef.current?.querySelector('.drop-indicator-slides');
        if (indicator) {
            indicator.remove();
        }
    };

    const getDragAfterElement = (container, y) => {
        if (!container) return null;
        const draggableElements = [...container.querySelectorAll('.message-list-item-slides:not(.opacity-50)')];
        return draggableElements.reduce((closest, child) => {
            const box = child.getBoundingClientRect();
            const offset = y - box.top - box.height / 2;
            if (offset < 0 && offset > closest.offset) {
                return { offset: offset, element: child };
            } else {
                return closest;
            }
        }, { offset: Number.NEGATIVE_INFINITY }).element;
    };

    const handleDrop = (e) => {
        e.preventDefault();
        clearDropIndicator();
        if (draggedMessageId === null) return;
        const draggedMsgIndex = messages.findIndex(m => m.id === draggedMessageId);
        if (draggedMsgIndex === -1) return;
        const draggedMsg = messages[draggedMsgIndex];
        const newMessages = [...messages];
        newMessages.splice(draggedMsgIndex, 1);
        const afterElement = getDragAfterElement(messageListContainerRef.current, e.clientY);
        if (afterElement == null) {
            newMessages.push(draggedMsg);
        } else {
            const targetMsgId = parseInt(afterElement.dataset.messageId, 10);
            const targetIndex = newMessages.findIndex(m => m.id === targetMsgId);
            if (targetIndex !== -1) {
                newMessages.splice(targetIndex, 0, draggedMsg);
            } else {
                newMessages.push(draggedMsg);
            }
        }
        setMessages(newMessages);
        setDraggedMessageId(null);
    };


    // --- Script Upload Handler ---
    const handleScriptUpload = (event) => {
        const file = event.target.files[0];
        if (!file) {
            alert("Please select a file.");
            return;
        }
        if (!file.name.toLowerCase().endsWith('.json')) {
            alert("Please upload a valid JSON file (.json).");
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const content = e.target.result;
                const scriptData = JSON.parse(content);

                if (!Array.isArray(scriptData)) {
                    throw new Error("JSON data must be an array.");
                }

                let currentId = messageIdCounter;
                const newMessages = [];

                scriptData.forEach((item, index) => {
                    let sender = '';
                    let text = '';

                    if (typeof item === 'object' && item !== null) {
                         if (item.hasOwnProperty('Me')) {
                            sender = 'sent';
                            text = item.Me;
                         } else if (item.hasOwnProperty('Them')) {
                             sender = 'received';
                             text = item.Them;
                         } else if (item.hasOwnProperty('sender') && item.hasOwnProperty('text')) {
                             sender = item.sender.toLowerCase() === 'me' ? 'sent' : 'received';
                             text = item.text;
                         }
                    }

                    if (sender && text) {
                        newMessages.push({ id: currentId++, text: String(text), sender: sender, type: 'text' });
                    } else {
                        console.warn(`Skipping invalid item at index ${index}:`, item);
                    }
                });

                if (newMessages.length > 0) {
                     if (window.confirm(`Successfully parsed ${newMessages.length} messages. Do you want to replace the current messages with this script?`)) {
                        setMessages(newMessages);
                        setMessageIdCounter(currentId);
                        alert("Script loaded successfully!");
                    }
                } else {
                    alert("No valid messages found in the script. Please check the JSON format. \nExpected format: `[{\"Me\": \"Hi\"}, {\"Them\": \"Hello!\"}]`");
                }

            } catch (error) {
                console.error("Error parsing script file:", error);
                alert(`Failed to load script: ${error.message}. Please ensure it's a valid JSON file with the expected format.`);
            } finally {
                 if(scriptUploadRef.current) {
                    scriptUploadRef.current.value = "";
                 }
            }
        };

        reader.onerror = (error) => {
            console.error("Error reading file:", error);
            alert("An error occurred while reading the file.");
             if(scriptUploadRef.current) {
                scriptUploadRef.current.value = "";
             }
        };

        reader.readAsText(file);
    };

    // --- Image Message Handlers ---
    const handleImageFileChange = (event) => {
        setSelectedImageFiles(Array.from(event.target.files));
        event.target.value = null;
    };

    const handleAddImageMessages = async () => {
        if (selectedImageFiles.length === 0) {
            alert("Please select one or more image files first.");
            return;
        }

        const sender = imageSenderType;
        const filesToProcess = [...selectedImageFiles];
        setSelectedImageFiles([]);

        let currentId = messageIdCounter;

        const loadImage = (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new Image();
                    img.onload = () => resolve({
                        id: currentId++,
                        type: 'image',
                        sender: sender,
                        src: e.target.result,
                        imgObject: img,
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                        text: `[Image: ${file.name}]`,
                    });
                    img.onerror = (err) => {
                        console.error(`Error loading image ${file.name}:`, err);
                        reject(new Error(`Failed to load ${file.name}`));
                    };
                    img.src = e.target.result;
                };
                reader.onerror = (err) => {
                    console.error(`Error reading file ${file.name}:`, err);
                    reject(new Error(`Failed to read ${file.name}`));
                };
                reader.readAsDataURL(file);
            });
        };

        try {
            const loadedImages = await Promise.all(filesToProcess.map(loadImage));

            setMessages(prev => [...prev, ...loadedImages]);
            setMessageIdCounter(currentId);
            alert(`Added ${loadedImages.length} image(s) to the conversation.`);

        } catch (error) {
            console.error("Error loading one or more images:", error);
            alert(`Failed to load some images: ${error.message}. Please try again.`);
            setMessageIdCounter(currentId);
        }
    };


    // --- REWRITTEN: Generate Slides and Download as ZIP ---
    const handleGenerateSlides = async () => {
        if (messages.length === 0) {
            alert("No messages to generate slides from. Load a script first.");
            return;
        }
        if (backgroundImages.length === 0) {
            alert("Please upload at least one background image first.");
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
            alert("Canvas not ready.");
            return;
        }
        
        if (typeof JSZip === 'undefined') {
            alert("JSZip library is not loaded. Please ensure it's installed and imported.");
            return;
        }

        const allImagesLoaded = messages
            .filter(msg => msg.type === 'image')
            .every(msg => msg.imgObject && msg.imgObject.complete) &&
            backgroundImages.every(bg => bg.image && bg.image.complete && (!bg.profileImage || bg.profileImage.complete));


        if (!allImagesLoaded) {
            alert("Some images (messages, backgrounds, or profile pictures) are still loading. Please wait a moment and try again.");
            return;
        }
        
        alert(`Starting generation for ${backgroundImages.length} background(s). This may take a moment. Your download will begin when it's complete.`);

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = EXPORT_WIDTH;
        tempCanvas.height = EXPORT_HEIGHT;
        const ctx = tempCanvas.getContext('2d');
        const activeScaleFactor = EXPORT_WIDTH / canvas.width;

        const baseFontSizeForPreview = Math.max(12, Math.min(canvas.width * 0.045, canvas.height * 0.025));
        const dynamicFontSize = baseFontSizeForPreview * currentFontSizeMultiplier * activeScaleFactor;
        const fontSize = Math.floor(dynamicFontSize);
        const lineHeight = fontSize * 1.3;
        const bubblePaddingY = fontSize * 0.5;
        const bubblePaddingX = fontSize * 0.8;
        ctx.font = `500 ${fontSize}px Inter, sans-serif`;

        let areaWidth, areaHeight, startY, sidePadding, usableWidth, maxMessageY, messageSpacing;

        if (showPhoneOverlay) {
            const chatRectWidth = EXPORT_WIDTH * (phoneUiWidth / 100);
            const chatRectHeight = EXPORT_HEIGHT * (phoneUiHeight / 100);
            const pfpSize = chatRectWidth * 0.16;
            const titleFontSizeCalc = Math.floor(chatRectWidth * 0.05);
            const paddingScale = headerPaddingScale / 100;
            const headerPaddingY = (25 * activeScaleFactor) * paddingScale;
            const pfpTitleGap = (5 * activeScaleFactor) * paddingScale;
            const headerHeight = headerPaddingY + pfpSize + pfpTitleGap + titleFontSizeCalc + headerPaddingY;

            areaWidth = chatRectWidth;
            areaHeight = chatRectHeight - headerHeight;
            startY = 15 * activeScaleFactor;
            sidePadding = areaWidth * 0.04;
            usableWidth = areaWidth - (2 * sidePadding);
        } else {
            areaWidth = EXPORT_WIDTH;
            areaHeight = EXPORT_HEIGHT;
            startY = EXPORT_HEIGHT * (messageTopMargin / 100);
            sidePadding = EXPORT_WIDTH * 0.04;
            usableWidth = EXPORT_WIDTH - (2 * sidePadding);
        }

        const messageMaxWidth = usableWidth * 0.75;
        messageSpacing = areaHeight * 0.015;
        const bottomPadding = areaHeight * 0.04;
        maxMessageY = areaHeight - bottomPadding;

        let totalGeneratedCount = 0;
        const zip = new JSZip();
        const baseFolderName = (exportFilenameRef.current.value || `chat_slides_export`).replace(/\.zip$/i, '');
        const rootFolder = zip.folder(baseFolderName);

        for (const [index, background] of backgroundImages.entries()) {
            const allChunks = [];
            let currentChunk = [];
            let currentY = startY;
            let i = 0;
            let lastMessageAdded = null;

            while (i < messages.length) {
                const msg = messages[i];
                const msgHeight = calculateMessageHeightInternal(msg, ctx, messageMaxWidth, bubblePaddingX, lineHeight, fontSize, bubblePaddingY, imageScale);
                const heightWithSpacing = msgHeight + messageSpacing;

                if ((currentY + heightWithSpacing <= maxMessageY) || currentChunk.length === 0) {
                    currentChunk.push(msg);
                    currentY += heightWithSpacing;
                    lastMessageAdded = msg;
                    i++;
                } else {
                    if (currentChunk.length > 0) {
                        allChunks.push([...currentChunk]);
                    }
                    const overlapMsg = lastMessageAdded;
                    if (!overlapMsg) {
                        console.error("Could not determine overlap message, stopping.");
                        alert("An error occurred during splitting. Please check console.");
                        return;
                    }
                    const overlapHeight = calculateMessageHeightInternal(overlapMsg, ctx, messageMaxWidth, bubblePaddingX, lineHeight, fontSize, bubblePaddingY, imageScale);
                    currentChunk = [overlapMsg];
                    currentY = startY + overlapHeight + messageSpacing;
                    if (msg.id === overlapMsg.id) {
                        i++;
                    }
                }
            }

            if (currentChunk.length > 0) {
                allChunks.push([...currentChunk]);
            }

            if (allChunks.length === 0) {
                console.warn(`Could not generate any slides for background: ${background.name}. Script empty or messages too large?`);
                continue;
            }
            
            const bgFolder = rootFolder.folder(`Set ${index + 1}`);

            for (let j = 0; j < allChunks.length; j++) {
                const chunk = allChunks[j];
                const slideName = `${String(j + 1).padStart(2, '0')}.jpeg`;
                try {
                    // --- BUG FIX ---
                    // The `showHook` option now correctly checks the `showHookOverlay` state from the UI.
                    const exportCanvasElement = drawCanvas(true, chunk, { showHook: showHookOverlay && (j === 0), backgroundImage: background });
                    
                    if (!exportCanvasElement) {
                        console.error(`Failed to draw canvas for slide ${j + 1} with background ${background.name}`);
                        continue;
                    }
                    const dataURL = exportCanvasElement.toDataURL('image/jpeg', 0.9);
                    const imageData = dataURL.substring(dataURL.indexOf(',') + 1);
                    
                    bgFolder.file(slideName, imageData, { base64: true });
                    totalGeneratedCount++;
                } catch (error) {
                     console.error(`Error generating slide ${j + 1} for background ${background.name}:`, error);
                     alert(`Error generating slide ${j + 1} for background ${background.name}. Check console.`);
                }
            }
        }

        if (totalGeneratedCount > 0) {
            zip.generateAsync({ type: "blob" }).then(function(content) {
                const link = document.createElement('a');
                link.href = URL.createObjectURL(content);
                link.download = `${baseFolderName}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
                alert(`Successfully generated and initiated download for ${totalGeneratedCount} slides in an organized ZIP file!`);
            }, function (err) {
                alert(`Failed to create ZIP file: ${err.message}`);
            });
        } else {
            alert("No slides were generated. Check your script, settings, and ensure images are loaded.");
        }
    };


    // --- Resize Effect (DEBOUNCED) ---
    useEffect(() => {
        const canvasEl = canvasRef.current; const container = canvasEl.parentElement;
        const resizeCanvas = () => {
            if (container && canvasEl) {
                const aspectRatio = 9 / 16; let newWidth = container.clientWidth;
                let newHeight = newWidth / aspectRatio; canvasEl.width = newWidth; canvasEl.height = newHeight;
                drawCanvas();
            }
        };
        if (document.readyState === 'complete') { resizeCanvas(); } else { window.addEventListener('load', resizeCanvas); }
        const debouncedResize = debounce(resizeCanvas, 250);
        window.addEventListener('resize', debouncedResize);
        return () => { window.removeEventListener('load', resizeCanvas); window.removeEventListener('resize', debouncedResize); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawCanvas]);


    // --- JSX ---
    return (
        <div className="main-container">
            {/* Controls Panel */}
            <div className="controls-panel">
                <h1 className="main-title">Bulk Chat Slides Creator</h1>

                 {/* Background Image Section - MODIFIED FOR BULK UPLOAD */}
                <div className="section-container">
                    <h2 className="section-title">Background Image(s)</h2>
                    <label htmlFor="bulkImageUpload" className="label">Upload Multiple Images (Max 12):</label>
                    <input type="file" id="bulkImageUpload" ref={bulkImageUploadRef} accept="image/*" multiple onChange={handleBulkImageUpload} className="file-input" />
                    {backgroundImages.length > 0 && (
                        <div className="thumbnail-container">
                            <h3 className="thumbnail-title">Select Preview Background:</h3>
                            <div className="thumbnail-list">
                                {backgroundImages.map((bg, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedBgImageIndex(index)}
                                        className={`thumbnail-button ${selectedBgImageIndex === index ? 'selected' : ''}`}
                                    >
                                        <img src={bg.src} alt={bg.name} className="thumbnail-image" />
                                    </button>
                                ))}
                            </div>
                             <p className="image-name-text">
                                Current: {currentBackgroundData ? currentBackgroundData.name : 'None Selected'}
                            </p>
                        </div>
                    )}
                     {backgroundImages.length === 0 && (
                        <p className="image-name-text">No background images uploaded. Please select some.</p>
                    )}
                </div>

                 {/* Image Filters Section */}
                <div className="section-container">
                    <div onClick={() => setFiltersOpen(!filtersOpen)} className="collapsible-header">
                        Image Filters <span className={`caret ${filtersOpen ? 'open' : ''}`}>▼</span>
                    </div>
                    {filtersOpen && ( <div className="collapsible-content">
                            <div><label htmlFor="blur" className="range-label">Blur: {blurVal}px</label><input type="range" id="blur" ref={blurRangeRef} min="0" max="20" value={blurVal} onChange={(e) => setBlurVal(parseInt(e.target.value))} className="range-input" /></div>
                            <div><label htmlFor="brightness" className="range-label">Brightness: {brightnessVal}%</label><input type="range" id="brightness" ref={brightnessRangeRef} min="0" max="200" value={brightnessVal} onChange={(e) => setBrightnessVal(parseInt(e.target.value))} className="range-input" /></div>
                            <div><label htmlFor="contrast" className="range-label">Contrast: {contrastVal}%</label><input type="range" id="contrast" ref={contrastRangeRef} min="0" max="200" value={contrastVal} onChange={(e) => setContrastVal(parseInt(e.target.value))} className="range-input" /></div>
                            <div><label htmlFor="saturate" className="range-label">Saturation: {saturateVal}%</label><input type="range" id="saturate" ref={saturateRangeRef} min="0" max="200" value={saturateVal} onChange={(e) => setSaturateVal(parseInt(e.target.value))} className="range-input" /></div>
                            <div><label htmlFor="grayscale" className="range-label">Grayscale: {grayscaleVal}%</label><input type="range" id="grayscale" ref={grayscaleRangeRef} min="0" max="100" value={grayscaleVal} onChange={(e) => setGrayscaleVal(parseInt(e.target.value))} className="range-input" /></div>
                            <button onClick={() => { setBlurVal(0); setBrightnessVal(100); setContrastVal(100); setSaturateVal(100); setGrayscaleVal(0); }} className="button-base reset-filters-button">Reset Filters</button>
                        </div>)}
                </div>

                {/* Hook Overlay Section. */}
                <div className="section-container">
                    <div onClick={() => setHookOverlayOpen(!hookOverlayOpen)} className="collapsible-header">
                        Hook Overlay <span className={`caret ${hookOverlayOpen ? 'open' : ''}`}>▼</span>
                    </div>
                    {hookOverlayOpen && ( <div className="collapsible-content">
                        <label className="checkbox-label">
                            <input type="checkbox" ref={hookOverlayToggleRef} checked={showHookOverlay} onChange={(e) => setShowHookOverlay(e.target.checked)} className="checkbox" />
                            Show Hook Overlay
                        </label>
                        {showHookOverlay && ( <div className="hook-overlay-controls">
                             <div className="button-group">
                                <button onClick={() => setHookMode('light')} className={`preset-button ${hookMode === 'light' ? 'active' : ''}`}>☀️ Light</button>
                                <button onClick={() => setHookMode('dark')} className={`preset-button ${hookMode === 'dark' ? 'active' : ''}`}>🌙 Dark</button>
                            </div>
                            <div>
                                <label htmlFor="hookText" className="label">Hook Text:</label>
                                <textarea id="hookText" ref={hookTextRef} value={hookText} onChange={(e) => setHookText(e.target.value)} rows="2" className="input-base" placeholder="Enter your hook text..."></textarea>
                            </div>
                             <div>
                                <label htmlFor="hookVerticalPosition" className="range-label">Vertical Position: {hookVerticalPosition}%</label>
                                <input
                                    type="range"
                                    id="hookVerticalPosition"
                                    ref={hookVerticalPositionSliderRef}
                                    min="0" max="100" // 0% = Top, 100% = Bottom
                                    value={hookVerticalPosition}
                                    onChange={(e) => setHookVerticalPosition(parseInt(e.target.value))}
                                    className="range-input"
                                />
                            </div>
                            <div>
                                <label htmlFor="hookOpacity" className="range-label">Opacity: {hookOpacity}%</label>
                                <input type="range" id="hookOpacity" ref={hookOpacitySliderRef} min="10" max="100" value={hookOpacity} onChange={(e) => setHookOpacity(parseInt(e.target.value))} className="range-input" />
                            </div>
                             <div>
                                <label htmlFor="hookFontSizeMultiplier" className="range-label">Font Size Scale: {Math.round(hookFontSizeMultiplier * 100)}%</label>
                                <input type="range" id="hookFontSizeMultiplier" ref={hookFontSizeMultiplierSliderRef} min="50" max="250" value={hookFontSizeMultiplier * 100} onChange={(e) => setHookFontSizeMultiplier(parseFloat(e.target.value) / 100)} className="range-input" />
                             </div>
                        </div>)}
                    </div>)}
                </div>


                {/* Phone UI Section */}
                <div className="section-container">
                    <div onClick={() => setPhoneUIOpen(!phoneUIOpen)} className="collapsible-header">
                        Phone UI Overlay <span className={`caret ${phoneUIOpen ? 'open' : ''}`}>▼</span>
                    </div>
                    {phoneUIOpen && ( <div className="collapsible-content">
                            <label className="checkbox-label">
                                <input type="checkbox" ref={phoneOverlayToggleRef} checked={showPhoneOverlay} onChange={(e) => setShowPhoneOverlay(e.target.checked)} className="checkbox" />
                                Show Phone UI Overlay
                            </label>
                            <div className="button-group">
                                <button onClick={() => applyPreset('light')} className="preset-button">☀️ Apply Light Mode</button>
                                <button onClick={() => applyPreset('dark')} className="preset-button">🌙 Apply Dark Mode</button>
                            </div>
                            {showPhoneOverlay && currentBackgroundData && ( <div className="phone-ui-controls">
                                    <div>
                                        <label htmlFor="chatTitle" className="label">Chat Title (for current background):</label>
                                        <input type="text" id="chatTitle" value={currentBackgroundData.chatTitle || ''} onChange={handleChatTitleChangeForBg} className="input-base" />
                                    </div>
                                    <div>
                                        <label htmlFor="profileImageUpload" className="label">Profile Image (for current background):</label>
                                        <input type="file" id="profileImageUpload" ref={profileImageUploadRef} accept="image/*" onChange={handleProfileImageUploadForBg} className="file-input" />
                                        {currentBackgroundData.profileImage && <img src={currentBackgroundData.profileImage.src} alt="PFP" className="profile-image-preview"/>}
                                    </div>
                                     <div className="color-picker-grid">
                                        <label htmlFor="headerColor" className="color-picker-label">Header: <input type="color" id="headerColor" ref={headerColorPickerRef} value={headerColor} onChange={(e) => setHeaderColor(e.target.value)} className="color-input" /> </label>
                                        <label htmlFor="chatBgColor" className="color-picker-label">Chat BG: <input type="color" id="chatBgColor" ref={chatBgColorPickerRef} value={chatBgColor} onChange={(e) => setChatBgColor(e.target.value)} className="color-input" /> </label>
                                        <label htmlFor="iconColorUI" className="color-picker-label">Icons: <input type="color" id="iconColorUI" value={iconColor} onChange={(e) => setIconColor(e.target.value)} className="color-input" /> </label>
                                        <label htmlFor="titleColorUI" className="color-picker-label">Title: <input type="color" id="titleColorUI" value={titleColor} onChange={(e) => setTitleColor(e.target.value)} className="color-input" /> </label>
                                    </div>
                                     <div>
                                        <label htmlFor="chatBgOpacity" className="range-label">Chat BG Opacity: {chatBgOpacity}%</label>
                                        <input type="range" id="chatBgOpacity" ref={chatBgOpacitySliderRef} min="0" max="100" value={chatBgOpacity} onChange={(e) => setChatBgOpacity(parseInt(e.target.value))} className="range-input" />
                                    </div>
                                     <div>
                                        <label htmlFor="phoneUiWidth" className="range-label"> Overlay Width: {phoneUiWidth}% </label>
                                        <input type="range" id="phoneUiWidth" ref={phoneUiWidthSliderRef} min="50" max="100" value={phoneUiWidth} onChange={(e) => setPhoneUiWidth(parseInt(e.target.value))} className="range-input" />
                                    </div>
                                     <div>
                                        <label htmlFor="phoneUiHeight" className="range-label"> Overlay Height: {phoneUiHeight}% </label>
                                        <input type="range" id="phoneUiHeight" ref={phoneUiHeightSliderRef} min="40" max="90" value={phoneUiHeight} onChange={(e) => setPhoneUiHeight(parseInt(e.target.value))} className="range-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="phoneUiHeaderPadding" className="range-label"> Header Padding Scale: {headerPaddingScale}% </label>
                                        <input type="range" id="phoneUiHeaderPadding" ref={phoneUiHeaderPaddingSliderRef} min="50" max="150" value={headerPaddingScale} onChange={(e) => setHeaderPaddingScale(parseInt(e.target.value))} className="range-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="phoneUiOffsetY" className="range-label"> Vertical Offset: {phoneUiOffsetY}% </label>
                                        <input type="range" id="phoneUiOffsetY" ref={phoneUiOffsetYSliderRef} min="-20" max="20" value={phoneUiOffsetY} onChange={(e) => setPhoneUiOffsetY(parseInt(e.target.value))} className="range-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="iconOffsetY" className="range-label"> Icon Vertical Offset: {currentBackgroundData.iconOffsetY || 0}px </label>
                                        <input type="range" id="iconOffsetY" ref={iconOffsetYSliderRef} min="-20" max="20" value={currentBackgroundData.iconOffsetY || 0} onChange={handleIconOffsetYChangeForBg} className="range-input" />
                                    </div>
                                </div>)}
                             <p className="disabled-text">
                                {showPhoneOverlay ? "When overlay is ON, 'Message Start Position' is disabled." : "'Message Start Position' below controls the top margin."}
                            </p>
                            {!currentBackgroundData && showPhoneOverlay && (
                                <p className="error-text">
                                    Please upload and select a background image to enable per-background UI settings.
                                </p>
                            )}
                        </div>)}
                </div>

                {/* Script Upload Section */}
                 <div className="section-container">
                    <h2 className="section-title">Load Script</h2>
                    <label htmlFor="scriptUpload" className="label">Upload JSON Script:</label>
                    <input type="file" id="scriptUpload" ref={scriptUploadRef} accept=".json" onChange={handleScriptUpload} className="file-input" />
                    <p className="script-format-text">
                        Format: `[{"Me: Hi"}, {"Them: Hello"}]`
                    </p>
                </div>

                {/* Image Upload Section */}
                <div className="section-container">
                    <div onClick={() => setImageUploadOpen(!imageUploadOpen)} className="collapsible-header">
                        Add Image Messages <span className={`caret ${imageUploadOpen ? 'open' : ''}`}>▼</span>
                    </div>
                    {imageUploadOpen && ( <div className="collapsible-content">
                        <label htmlFor="imageMessageUpload" className="label">Upload Image(s):</label>
                        <input type="file" id="imageMessageUpload" ref={imageMessageUploadRef} multiple accept="image/*" onChange={handleImageFileChange} className="file-input" />

                        {selectedImageFiles.length > 0 && (
                            <div className="selected-images-container">
                                <p className="selected-images-title">Selected ({selectedImageFiles.length}):</p>
                                <div className="selected-images-grid">
                                    {selectedImageFiles.map((file, index) => (
                                        <img key={index} src={URL.createObjectURL(file)} alt={`preview ${index}`} className="selected-image-preview"/>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="sender-type-container">
                            <span className="label">Send As:</span>
                            <div className="button-group">
                                <button onClick={() => setImageSenderType('received')} className={`preset-button ${imageSenderType === 'received' ? 'active-purple' : ''}`}>Them</button>
                                <button onClick={() => setImageSenderType('sent')} className={`preset-button ${imageSenderType === 'sent' ? 'active-sky' : ''}`}>Me</button>
                            </div>
                        </div>

                        <button onClick={handleAddImageMessages} className="button-base primary-button full-width" disabled={selectedImageFiles.length === 0}>
                            Add {selectedImageFiles.length > 0 ? selectedImageFiles.length : ''} Image(s) to Conversation
                        </button>
                    </div>)}
                </div>


                {/* Current Messages Section */}
                <div className="section-container">
                    <h3 className="section-subtitle">Current Messages (Drag to reorder):</h3>
                    <div ref={messageListContainerRef} onDragOver={handleDragOver} onDrop={handleDrop} onDragLeave={clearDropIndicator} className="message-list-container" >
                        {messages.length === 0 ? ( <p className="no-messages-text">No messages yet. Try loading a script.</p>
                        ) : ( messages.map(msg => ( <div key={msg.id} draggable onDragStart={(e) => handleDragStart(e, msg.id)} onDragEnd={handleDragEnd} data-message-id={msg.id} className="message-list-item-slides" >
                           <span className={`message-text ${msg.sender === 'sent' ? 'sent' : 'received'}`}>
                               {msg.sender === 'sent' ? 'Me: ' : 'Them: '}
                               {msg.type === 'image' ? <span className='image-placeholder-text'>[Image]</span> : `${msg.text.substring(0, 25)}${msg.text.length > 25 ? '...' : ''}`}
                           </span>
                           <button onClick={(e) => { e.stopPropagation(); setMessages(prev => prev.filter(m => m.id !== msg.id)); }} className="remove-message-button" > &times; </button>
                         </div> )) )}
                    </div>
                    <div className="button-group">
                        <button onClick={handleRemoveLastMessage} className="button-base orange-button">Remove Last</button>
                        <button onClick={handleClearAllMessages} className="button-base red-button">Clear All</button>
                    </div>
                </div>

                {/* Message Styling Section */}
                <div className="section-container">
                     <div onClick={() => setStylingOpen(!stylingOpen)} className="collapsible-header"> Message Styling <span className={`caret ${stylingOpen ? 'open' : ''}`}>▼</span> </div>
                     {stylingOpen && (
                        <div className="collapsible-content">
                             <p className="disabled-text">These colors can be set manually or via the Light/Dark presets above.</p>
                            <div className="color-picker-grid">
                                <div> <h3 className="color-picker-title">Sent (Me)</h3> <label htmlFor="sentBubbleColor" className="color-picker-label">Bubble: <input type="color" id="sentBubbleColor" ref={sentBubbleColorPickerRef} value={msgStyles.sent.bubble} onChange={(e) => setMsgStyles(s => ({ ...s, sent: { ...s.sent, bubble: e.target.value } }))} className="color-input" /> </label> <label htmlFor="sentFontColor" className="color-picker-label">Font: <input type="color" id="sentFontColor" ref={sentFontColorPickerRef} value={msgStyles.sent.font} onChange={(e) => setMsgStyles(s => ({ ...s, sent: { ...s.sent, font: e.target.value } }))} className="color-input" /> </label> </div>
                                <div> <h3 className="color-picker-title">Received (Them)</h3> <label htmlFor="receivedBubbleColor" className="color-picker-label">Bubble: <input type="color" id="receivedBubbleColor" ref={receivedBubbleColorPickerRef} value={msgStyles.received.bubble} onChange={(e) => setMsgStyles(s => ({ ...s, received: { ...s.received, bubble: e.target.value } }))} className="color-input" /> </label> <label htmlFor="receivedFontColor" className="color-picker-label">Font: <input type="color" id="receivedFontColor" ref={receivedFontColorPickerRef} value={msgStyles.received.font} onChange={(e) => setMsgStyles(s => ({ ...s, received: { ...s.received, font: e.target.value } }))} className="color-input" /> </label> </div>
                            </div>
                            <div> <label htmlFor="fontSizeMultiplier" className="range-label">Text Font Size Scale: {Math.round(currentFontSizeMultiplier * 100)}%</label> <input type="range" id="fontSizeMultiplier" ref={fontSizeMultiplierSliderRef} min="50" max="200" value={currentFontSizeMultiplier * 100} onChange={(e) => setCurrentFontSizeMultiplier(parseFloat(e.target.value) / 100)} className="range-input" /> </div>
                             <div>
                                <label htmlFor="imageScale" className="range-label">Image Message Scale: {imageScale}%</label>
                                <input type="range" id="imageScale" ref={imageScaleSliderRef} min="30" max="100" value={imageScale} onChange={(e) => setImageScale(parseInt(e.target.value))} className="range-input" />
                             </div>
                            <div> <label htmlFor="bubbleRoundness" className="range-label">Bubble/Image Roundness: {bubbleRoundness}px</label> <input type="range" id="bubbleRoundness" ref={bubbleRoundnessSliderRef} min="0" max="150" value={bubbleRoundness} onChange={(e) => setBubbleRoundness(parseInt(e.target.value))} className="range-input" /> </div>
                            <div> <label htmlFor="messageTopMargin" className={`range-label ${showPhoneOverlay ? 'disabled' : ''}`}> Message Start Position (Top %): {messageTopMargin}% {showPhoneOverlay ? '(Disabled)' : ''} </label> <input type="range" id="messageTopMargin" ref={messageTopMarginSliderRef} min="0" max="80" value={messageTopMargin} onChange={(e) => setMessageTopMargin(parseInt(e.target.value))} className={`range-input ${showPhoneOverlay ? 'disabled-input' : ''}`} disabled={showPhoneOverlay} /> </div>
                        </div>
                     )}
                </div>

                {/* Add Message Section */}
                <div className="section-container">
                    <h2 className="section-title">Add Text Message (Manual)</h2>
                    <div> <label htmlFor="messageText" className="label">Message Text:</label> <textarea id="messageText" ref={messageTextRef} rows="3" className="input-base" placeholder="Enter message text..."></textarea> </div>
                    <div className="message-type-container"> <span className="label">Type: Received (Them) / Sent (Me)</span> <label className="toggle-switch"> <input type="checkbox" ref={messageTypeToggleRef} className="toggle-input" /> <div className="toggle-slider"></div> </label> </div>
                    <button onClick={handleAddMessage} className="button-base primary-button full-width">Add Text Message</button>
                </div>

                 {/* MODIFIED: Slide Generation Section for ZIP export */}
                <div className="section-container">
                    <h2 className="section-title">Generate Slides (Auto-Split)</h2>
                    <p className="description-text">
                        This will automatically split the script into multiple images for <strong className="highlight-amber">each</strong> uploaded background.
                        The results will be compiled into a single <strong className="highlight-sky">.ZIP</strong> file with a main folder and subfolders named "Set 1", "Set 2", etc.
                        The Hook Overlay (if enabled) only appears on the first slide of each set.
                    </p>
                    <button onClick={handleGenerateSlides} className="button-base green-button full-width" disabled={backgroundImages.length === 0}>
                        Generate & Download All Slides as ZIP
                    </button>
                     <p className="disabled-text">
                        Adjust Font/Overlay/Hook/Image settings for best results before generating.
                    </p>
                </div>

                {/* Save Queue Section */}
                <div className="section-container">
                    <h2 className="section-title">Manual Save Queue ({imageQueue.length} images)</h2>
                    {imageQueue.length === 0 ? ( <p className="no-messages-text">Queue is empty. Use the 'Prepare & Export' section to add images manually.</p> ) : ( <div className="queue-list-container"> {imageQueue.map((image, index) => ( <div key={index} className="queue-item" > <span className="queue-item-name" title={`${image.name}.jpeg`}>{image.name}.jpeg</span> <button onClick={() => handleRemoveFromQueue(index)} className="remove-queue-item-button" title="Remove from queue" > &times; </button> </div> ))} </div> )}
                    {imageQueue.length > 0 && ( <div className="queue-buttons-container"> <button onClick={handleDownloadQueue} className="button-base green-button" > Download All ({imageQueue.length}) from Queue </button> <button onClick={() => { if (window.confirm("Are you sure you want to clear the entire save queue?")) { setImageQueue([]); } }} className="button-base red-button" > Clear Queue </button> </div> )}
                </div>

                {/* Export Section */}
                <div className="export-section">
                    <h2 className="section-title">Prepare & Export Image</h2>
                    <div> <label htmlFor="currentImageName" className="label">Image Name (for Manual Add/Single Download):</label> <input type="text" id="currentImageName" value={currentImageName} onChange={(e) => setCurrentImageName(e.target.value)} placeholder="Enter name for manual add..." className="input-base" /> </div>
                    <button onClick={handleAddImageToQueue} className="button-base blue-button full-width" disabled={!currentImageName.trim() || !currentBackgroundData} > Add Current View to Manual Queue </button>
                    <div className="export-filename-container"> <label htmlFor="exportFilename" className="label">Base Filename (for ZIP or Single Export):</label> <input type="text" id="exportFilename" ref={exportFilenameRef} value={exportFilename.replace(/\.(png|jpeg|jpg|zip)$/i, '')} onChange={(e) => setExportFilename(e.target.value)} placeholder="Enter base filename..." className="input-base" /> <button onClick={handleExportImage} className="button-base primary-button full-width" disabled={!currentBackgroundData}> Download Current View Immediately (JPEG) </button> </div>
                </div>
            </div>

            {/* Canvas Preview Area */}
            <div className="canvas-preview-area">
                <div className="canvas-container">
                    <canvas ref={canvasRef} className="canvas"></canvas>
                </div>
                <p className="preview-text">Preview (9:16 Aspect Ratio)</p>
            </div>
        </div>
    );
};

export default BulkChatSlidesPage;